/**
 * ═══════════════════════════════════════════════════════════════════
 * PlaySnowman Golf — Email Capture Widget
 * ═══════════════════════════════════════════════════════════════════
 *
 * FEATURES
 * • Exit-intent detection (desktop) — triggers when mouse leaves viewport
 * • Scroll-up trigger (mobile) — triggers when user scrolls up past threshold
 * • 15% off incentive with email + product interest capture
 * • localStorage persistence with JSON export
 * • GA4 event tracking hooks (dataLayer.push)
 * • Cookie-less session management via sessionStorage
 * • Respects reduced-motion preference
 *
 * SHOPIFY INSTALLATION
 * Paste this entire file contents into theme.liquid before </body>,
 * or upload as an asset and link: <script src="{{ 'email-capture.js' | asset_url }}">
 * ═══════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  /* ── Configuration ── */
  const CONFIG = {
    mobileScrollThreshold: 0.40,     // Trigger after scrolling past 40% of page
    mobileScrollUpDelta: 80,         // Pixels of upward scroll to trigger
    cooldownHours: 24,               // Hours before showing popup again
    storageKey: 'ps_email_capture', // localStorage key
    sessionKey: 'ps_popup_shown',   // sessionStorage key
    gaEventPrefix: 'ps_email_capture',
  };

  /* ── State ── */
  let state = {
    shown: false,
    submitted: false,
    lastScrollY: window.scrollY || 0,
    maxScrollY: window.scrollY || 0,
    scrollUpAccum: 0,
  };

  /* ── DOM refs ── */
  const overlay = document.getElementById('ps-email-capture');
  const form = document.getElementById('ps-form');
  const emailInput = document.getElementById('ps-email');
  const interestSelect = document.getElementById('ps-interest');
  const closeBtn = overlay?.querySelector('.ps-close');

  if (!overlay || !form) return; // Guard: widget HTML not present

  /* ═══════════════════════════════════════════════════════════════
     GA4 TRACKING HELPERS
     Pushes events to dataLayer for Google Tag Manager / GA4.
     If gtag is not defined, falls back to dataLayer.push.
     ═══════════════════════════════════════════════════════════════ */
  function trackEvent(eventName, params = {}) {
    const fullEventName = `${CONFIG.gaEventPrefix}_${eventName}`;
    const payload = {
      event: fullEventName,
      ps_widget: 'email_capture',
      ...params,
    };

    // dataLayer (GTM)
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push(payload);
    }

    // gtag direct (GA4)
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, {
        event_category: 'ps_email_capture',
        ...params,
      });
    }

    // Debug fallback
    if (window.location.search.includes('ps-debug')) {
      console.log('[PS Track]', fullEventName, params);
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     STORAGE / DATA MANAGEMENT
     ═══════════════════════════════════════════════════════════════ */

  function getStorage() {
    try {
      const raw = localStorage.getItem(CONFIG.storageKey);
      return raw ? JSON.parse(raw) : { entries: [], lastExport: null };
    } catch (e) {
      return { entries: [], lastExport: null };
    }
  }

  function setStorage(data) {
    try {
      localStorage.setItem(CONFIG.storageKey, JSON.stringify(data));
    } catch (e) {
      console.warn('[PS] localStorage write failed:', e);
    }
  }

  function saveEntry(email, interest) {
    const data = getStorage();
    const entry = {
      email: email.toLowerCase().trim(),
      interest: interest,
      source: isMobile() ? 'scroll_up' : 'exit_intent',
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    data.entries.push(entry);
    setStorage(data);
    return entry;
  }

  /* ═══════════════════════════════════════════════════════════════
     EXPORT FUNCTION — call from browser console:
     PlaySnowmanEmailCapture.exportJSON()
     ═══════════════════════════════════════════════════════════════ */
  function exportJSON() {
    const data = getStorage();
    const blob = new Blob(
      [JSON.stringify(data, null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `playsnowman-emails-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    data.lastExport = new Date().toISOString();
    setStorage(data);
    return data;
  }

  /* ── Check if already captured this email (per session) ── */
  function hasSubmittedThisSession() {
    return sessionStorage.getItem(CONFIG.sessionKey + '_submitted') === '1';
  }

  /* ── Check cooldown (has popup been shown recently?) ── */
  function isOnCooldown() {
    const lastShown = localStorage.getItem(CONFIG.storageKey + '_last_shown');
    if (!lastShown) return false;
    const hoursSince = (Date.now() - parseInt(lastShown, 10)) / (1000 * 60 * 60);
    return hoursSince < CONFIG.cooldownHours;
  }

  /* ═══════════════════════════════════════════════════════════════
     POPUP DISPLAY LOGIC
     ═══════════════════════════════════════════════════════════════ */

  function showPopup(source) {
    if (state.shown || state.submitted) return;
    if (hasSubmittedThisSession()) return;
    if (isOnCooldown()) return;

    state.shown = true;
    overlay.classList.add('ps-active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Record show timestamp
    localStorage.setItem(CONFIG.storageKey + '_last_shown', Date.now().toString());
    sessionStorage.setItem(CONFIG.sessionKey, '1');

    trackEvent('popup_shown', { trigger_source: source });
  }

  function closePopup(reason = 'manual') {
    if (!state.shown) return;
    state.shown = false;
    overlay.classList.remove('ps-active');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    trackEvent('popup_closed', { close_reason: reason });
  }

  /* ═══════════════════════════════════════════════════════════════
     SUCCESS STATE
     ═══════════════════════════════════════════════════════════════ */

  function showSuccess(email) {
    state.submitted = true;
    sessionStorage.setItem(CONFIG.sessionKey + '_submitted', '1');

    const content = overlay.querySelector('.ps-popup-content');
    const visual = overlay.querySelector('.ps-popup-visual');

    if (content) {
      content.innerHTML = `
        <div class="ps-success-state ps-visible">
          <div class="ps-success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div class="ps-success-title">You're On the Tee Sheet</div>
          <p class="ps-success-msg">Check your inbox — a welcome note is heading to <strong>${escapeHtml(email)}</strong>.</p>
          <p class="ps-success-note">⛳ Pro tip: build your 18-Hole Kit — any 3+ items get 15% off automatically at checkout.</p>
        </div>
      `;
    }
    if (visual) {
      visual.style.background = 'linear-gradient(135deg, #06281c 0%, #0f5132 50%, #06281c 100%)';
    }

    // Auto-close after 6 seconds
    setTimeout(() => closePopup('auto_after_success'), 6000);
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /* ═══════════════════════════════════════════════════════════════
     EXIT INTENT (Desktop)
     Fires when mouse leaves the viewport toward the top (URL/tab bar)
     ═══════════════════════════════════════════════════════════════ */

  function isMobile() {
    return window.matchMedia('(pointer: coarse)').matches ||
           window.innerWidth < 768 ||
           'ontouchstart' in window;
  }

  function initExitIntent() {
    if (isMobile()) return; // Desktop only

    document.addEventListener('mouseleave', function (e) {
      if (e.clientY < 10 && e.relatedTarget === null) {
        showPopup('exit_intent');
      }
    }, { once: true });
  }

  /* ═══════════════════════════════════════════════════════════════
     SCROLL-UP TRIGGER (Mobile)
     Fires when user scrolls up after having scrolled past threshold
     ═══════════════════════════════════════════════════════════════ */

  function initScrollTrigger() {
    if (!isMobile()) return; // Mobile only

    const docHeight = () => document.documentElement.scrollHeight - window.innerHeight;

    window.addEventListener('scroll', function () {
      const currentY = window.scrollY || window.pageYOffset;
      const scrollPercent = docHeight() > 0 ? currentY / docHeight() : 0;

      // Track max scroll
      if (currentY > state.maxScrollY) {
        state.maxScrollY = currentY;
      }

      // Accumulate upward scroll
      if (currentY < state.lastScrollY) {
        state.scrollUpAccum += state.lastScrollY - currentY;
      } else {
        state.scrollUpAccum = 0; // Reset on downward scroll
      }

      state.lastScrollY = currentY;

      // Trigger: past threshold AND significant upward scroll
      if (scrollPercent > CONFIG.mobileScrollThreshold &&
          state.scrollUpAccum > CONFIG.mobileScrollUpDelta) {
        showPopup('scroll_up');
        // Remove listener after trigger
        window.removeEventListener('scroll', arguments.callee);
      }
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════════════════════════
     FORM HANDLING
     ═══════════════════════════════════════════════════════════════ */

  function validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();

    const email = emailInput.value.trim();
    const interest = interestSelect.value;
    let valid = true;

    // Reset errors
    emailInput.closest('.ps-field').classList.remove('ps-invalid');
    interestSelect.closest('.ps-field').classList.remove('ps-invalid');

    if (!validateEmail(email)) {
      emailInput.closest('.ps-field').classList.add('ps-invalid');
      valid = false;
    }
    if (!interest) {
      interestSelect.closest('.ps-field').classList.add('ps-invalid');
      valid = false;
    }

    if (!valid) {
      trackEvent('form_error', { error_type: valid ? '' : 'validation_failed' });
      return;
    }

    // Save data
    const entry = saveEntry(email, interest);

    // Sync to Mike's email list (Brevo via revenue.michaelgaff.com)
    fetch('https://revenue.michaelgaff.com/api/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, store: 'snowman', unit: interest }),
    }).catch(() => { /* offline is fine — local copy kept */ });

    // Track
    trackEvent('form_submit', {
      interest_category: interest,
      has_gtag: typeof window.gtag === 'function',
      has_dataLayer: typeof window.dataLayer !== 'undefined',
    });

    // Show success
    showSuccess(email);
  });

  // Clear error on input
  emailInput.addEventListener('input', function () {
    this.closest('.ps-field').classList.remove('ps-invalid');
  });
  interestSelect.addEventListener('change', function () {
    this.closest('.ps-field').classList.remove('ps-invalid');
  });

  /* ═══════════════════════════════════════════════════════════════
     CLOSE HANDLERS
     ═══════════════════════════════════════════════════════════════ */

  closeBtn?.addEventListener('click', () => closePopup('close_button'));

  // Close on backdrop click
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) closePopup('backdrop_click');
  });

  // Close on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && state.shown) closePopup('escape_key');
  });

  /* ═══════════════════════════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════════════════════════ */

  // Don't init if already submitted or shown this session
  if (hasSubmittedThisSession() || sessionStorage.getItem(CONFIG.sessionKey)) {
    return;
  }

  // Small delay so page metrics can initialize first
  setTimeout(function () {
    initExitIntent();
    initScrollTrigger();
  }, 1200);

  /* ── Expose public API ── */
  window.PlaySnowmanEmailCapture = {
    show: () => showPopup('manual_api'),
    close: () => closePopup('manual_api'),
    exportJSON: exportJSON,
    getData: getStorage,
    clearData: () => { localStorage.removeItem(CONFIG.storageKey); },
  };

})();
